import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Question } from '@phosphor-icons/react'
import GuidanceNote from '../../../components/GuidanceNote'
import {
  applyPresetToScope,
  groupScopesForModule,
  includesPermissionActions,
  samePermissionActions,
  scopesForModule
} from '../../utils/permissions'
import {
  permissionApplyAllBarSx,
  permissionApplyAllLabelSx,
  permissionEmptyStateSx,
  permissionGroupRowSx,
  permissionModulePanelSx,
  permissionModuleTitleSx,
  permissionScopeDescriptionSx,
  permissionScopeGridSx,
  permissionScopeTitleSx,
  permissionSpecialBadgeSx,
  permissionSpecialScopeSx,
  RBAC_PERMISSION_HELP_ACCENT_COLOR,
  RBAC_PERMISSION_HELP_ICON_COLOR,
  scopeDescription,
  scopeStateKey,
  scopeTitle
} from './roleWizardConstants'
import {
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_SURFACE_INFO,
  UI_COLOR_TEXT_INFO
} from '../../../constants/uiColors'

const PRESET_OPTIONS = [
  {
    key: 'view',
    label: 'Solo ver',
    action: 'READ',
    legacyAction: 'view',
    icon: VisibilityOutlinedIcon
  },
  {
    key: 'download',
    label: 'Descargar',
    action: 'DOWNLOADS',
    legacyAction: 'download',
    icon: CloudDownloadOutlinedIcon
  },
  {
    key: 'edit',
    label: 'Todo editar',
    action: 'READ_WRITE',
    legacyAction: 'edit',
    icon: EditOutlinedIcon
  }
]

const GLOBAL_PRESET_OPTIONS = [
  ...PRESET_OPTIONS,
  { key: 'clear', label: 'Limpiar', icon: CleaningServicesOutlinedIcon }
]

function assignableActions(scope) {
  return (scope?.actions || []).filter((action) => action !== 'OFF')
}

function availablePresets(scope) {
  const actions = assignableActions(scope)
  return PRESET_OPTIONS.filter((option) => actions.includes(option.action))
}

function presetClassName(option, active, readOnly = false) {
  return [
    'role-wizard__preset',
    `role-wizard__preset--${option.key}`,
    active ? 'is-active' : '',
    readOnly ? 'is-read-only' : ''
  ]
    .filter(Boolean)
    .join(' ')
}

function isPresetActive(selectedActions = [], option) {
  return selectedActions.includes(option.action) || selectedActions.includes(option.legacyAction)
}

function PresetIcon({ icon: Icon }) {
  return <Icon className="role-wizard__preset-icon" fontSize="small" />
}

function PresetLabel({ icon: Icon, label }) {
  return (
    <span className="role-wizard__preset-content">
      <PresetIcon icon={Icon} />
      <span>{label}</span>
    </span>
  )
}

function globalPresetIsActive(modules, permissionsByScope, preset) {
  if (preset === 'clear') return false

  const scopes = modules.flatMap((module) =>
    scopesForModule(module).map((scope) => ({ moduleKey: module.key, scope }))
  )
  if (scopes.length === 0) return false

  return scopes.every(({ moduleKey, scope }) => {
    const selectedActions = permissionsByScope[scopeStateKey(moduleKey, scope.key)] || []
    const requiredActions = applyPresetToScope(scope.actions, preset)

    if (includesPermissionActions(selectedActions, requiredActions)) return true
    if (preset === 'view') {
      return (
        includesPermissionActions(selectedActions, applyPresetToScope(scope.actions, 'download')) ||
        includesPermissionActions(selectedActions, applyPresetToScope(scope.actions, 'edit'))
      )
    }
    if (preset === 'download') {
      return includesPermissionActions(selectedActions, applyPresetToScope(scope.actions, 'edit'))
    }

    return false
  })
}

export function RolePermissionPresetActions({
  scope,
  selectedActions,
  onApplyPreset,
  compact = false,
  readOnly = false,
  selectedOnly = false
}) {
  const options = availablePresets(scope).filter(
    (option) => !selectedOnly || isPresetActive(selectedActions, option)
  )
  if (options.length === 0) return null

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
      sx={{ minWidth: compact ? { md: 260 } : { md: 360 } }}
    >
      {options.map((option) => {
        const { key, label, icon: Icon } = option
        const active = isPresetActive(selectedActions, option)

        return (
          <Button
            key={key}
            variant="outlined"
            size="small"
            aria-pressed={active}
            disabled={readOnly}
            onClick={readOnly ? undefined : () => onApplyPreset(key)}
            className={presetClassName(option, active, readOnly)}
          >
            <PresetLabel icon={Icon} label={label} />
          </Button>
        )
      })}
    </Stack>
  )
}

function ScopeRow({
  moduleKey,
  scope,
  selectedActions,
  onApplyPreset,
  special = false,
  readOnly = false
}) {
  return (
    <Box
      sx={{
        ...permissionScopeGridSx,
        px: { xs: 2, md: 2.25 },
        py: special ? 1.6 : { xs: 1.75, md: 2 },
        ...(special ? permissionSpecialScopeSx : {})
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={permissionScopeTitleSx}>
          {scopeTitle(moduleKey, scope)}
        </Typography>
        <Typography sx={permissionScopeDescriptionSx}>
          {scopeDescription(moduleKey, scope)}
        </Typography>
      </Box>
      <RolePermissionPresetActions
        scope={scope}
        selectedActions={selectedActions}
        onApplyPreset={onApplyPreset}
        compact={special}
        readOnly={readOnly}
      />
    </Box>
  )
}

function PermissionGroup({
  moduleKey,
  group,
  permissionsByScope,
  onApplyScopePreset,
  readOnly = false
}) {
  const children = group.children || []
  if (!group.parentScope && children.length === 0) return null

  return (
    <Box sx={permissionGroupRowSx}>
      {group.parentScope && (
        <ScopeRow
          moduleKey={moduleKey}
          scope={group.parentScope}
          selectedActions={
            permissionsByScope[scopeStateKey(moduleKey, group.parentScope.key)] || []
          }
          onApplyPreset={(preset) => onApplyScopePreset(group.parentScope, preset)}
          readOnly={readOnly}
        />
      )}
      {children.length > 0 && (
        <Box sx={{ px: { xs: 2, md: 2.25 }, pb: 2 }}>
          <Typography component="span" sx={permissionSpecialBadgeSx}>
            Configuraciones especiales
          </Typography>
          <Stack spacing={1.25}>
            {children.map((scope) => (
              <ScopeRow
                key={scope.key}
                moduleKey={moduleKey}
                scope={scope}
                selectedActions={permissionsByScope[scopeStateKey(moduleKey, scope.key)] || []}
                onApplyPreset={(preset) => onApplyScopePreset(scope, preset)}
                special
                readOnly={readOnly}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

function ModuleSection({ module, permissionsByScope, onApplyScopePreset, readOnly = false }) {
  const groups = groupScopesForModule(module)
  if (groups.length === 0) return null

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.4 }}>
        <Typography sx={permissionModuleTitleSx}>
          {module.label}
        </Typography>
        <Tooltip title={`Permisos disponibles en ${module.label}`}>
          <Question size={16} color={RBAC_PERMISSION_HELP_ICON_COLOR} />
        </Tooltip>
      </Stack>
      <Paper
        variant="outlined"
        sx={permissionModulePanelSx}
      >
        {groups.map((group) => (
          <PermissionGroup
            key={group.parentScope?.key || group.key}
            moduleKey={module.key}
            group={group}
            permissionsByScope={permissionsByScope}
            onApplyScopePreset={(scope, preset) => onApplyScopePreset(module.key, scope, preset)}
            readOnly={readOnly}
          />
        ))}
      </Paper>
    </Box>
  )
}

export default function RoleWizardPermissionsStep({
  modules,
  permissionsByScope,
  onApplyAll,
  onApplyScopePreset,
  readOnly = false
}) {
  return (
    <Stack spacing={3.6}>
      <GuidanceNote
        icon={<InfoOutlinedIcon />}
        title=""
        message={
          'Selecciona el nivel de acceso de cada módulo. Algunos módulos incluyen configuraciones especiales que puedes otorgar por separado.'
        }
        accentColor={UI_COLOR_PRIMARY_HOVER}
        backgroundColor={UI_COLOR_SURFACE_INFO}
        textColor={UI_COLOR_TEXT_INFO}
        singleLine
        sx={{ mb: 2 }}
      />
      <Paper
        variant="outlined"
        sx={permissionApplyAllBarSx}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
          <Typography sx={permissionApplyAllLabelSx}>
            Aplicar a todos los módulos:
          </Typography>
          <Tooltip title="Aplica el nivel seleccionado a todos los módulos visibles">
            <Question size={16} color={RBAC_PERMISSION_HELP_ACCENT_COLOR} />
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {GLOBAL_PRESET_OPTIONS.map((option) => {
            const { key, label, icon: Icon } = option
            const active = globalPresetIsActive(modules, permissionsByScope, key)

            return (
              <Button
                key={key}
                variant="outlined"
                size="small"
                aria-pressed={active}
                disabled={readOnly}
                onClick={readOnly ? undefined : () => onApplyAll(key)}
                className={presetClassName(option, active, readOnly)}
              >
                <PresetLabel icon={Icon} label={label} />
              </Button>
            )
          })}
        </Stack>
      </Paper>

      {modules.length === 0 ? (
        <Paper variant="outlined" sx={permissionEmptyStateSx}>
          No hay módulos disponibles para configurar permisos.
        </Paper>
      ) : (
        <Stack spacing={3.2}>
          {modules.map((module) => (
            <ModuleSection
              key={module.key}
            module={module}
            permissionsByScope={permissionsByScope}
            onApplyScopePreset={onApplyScopePreset}
            readOnly={readOnly}
          />
        ))}
        </Stack>
      )}
    </Stack>
  )
}
